import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buildPathForLocale } from '@/lib/intl-seo';
import { toast } from 'sonner';

const RegistrationSuccessPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(location.state?.email || '');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const loginPath = buildPathForLocale((i18n.language || 'en').substring(0, 2), ['login']);

  const handleResend = async () => {
    if (!email) {
      toast.error(t('auth.enterEmail'));
      return;
    }

    setLoading(true);
    try {
      await auth.resendVerification(email);
      setResent(true);
      toast.success(t('auth.verificationResent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resendFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/10 p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-2xl shadow-xl border p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          
          <h1 className="text-2xl font-bold mb-2">{t('auth.checkYourEmail')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('auth.verificationEmailSent')}
          </p>

          {email && (
            <div className="bg-secondary/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-muted-foreground">{t('auth.sentTo')}</p>
              <p className="font-medium">{email}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p>{t('auth.didntReceiveEmail')}</p>
            </div>

            {!email && (
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterYourEmail')}
                />
              </div>
            )}

            <Button
              onClick={handleResend}
              disabled={loading || resent}
              variant="outline"
              className="w-full rounded-full"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : resent ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {resent ? t('auth.emailResent') : t('auth.resendEmail')}
            </Button>

            <Link to={loginPath}>
              <Button variant="ghost" className="w-full rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('auth.backToLogin')}
              </Button>
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t">
            <p className="text-xs text-muted-foreground">
              {t('auth.spamFolderNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccessPage;
